<script setup lang="ts">
import { computed } from 'vue'
import { getDashboardStatisticsAction } from '@/api/functions/dashboard/get-dashboard-statistics-action'
import { useQueryApi } from '@/composables/useApi'
import StatusSummaryCard from './partials/StatusSummaryCard.vue'

//  Fetch dashboard statistics data
const { data } = useQueryApi({
  queryKey: ['dashboardStatistics'],
  queryFn: getDashboardStatisticsAction,
  staleTime: 1000 * 60 * 5,
})

//  Map raw API data into an object for easier access
const stats = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const map: Record<string, any> = {}
  if (data.value?.data?.length) {
    for (const item of data.value.data) {
      map[item.slug] = item
    }
  }
  return map
})

//  Financial summary cards (top section)
const dashboardMetrics = computed(() => [
  {
    label: 'موجودی حساب درگاه',
    price: stats.value.sum_received_from_gateway?.value ?? 0,
    currency: 'TOMAN',
  },
  {
    label: 'تراکنش‌های در انتظار تسویه',
    price: stats.value.total_settle_with_seller_pending?.value ?? 0,
    currency: 'COUNT',
    linkLabel: 'مشاهده‌ی تراکنش‌ها',
    linkTo: '/transactions?status=SETTLE_WITH_SELLER_PENDING&page=1',
  },
  {
    label: 'تسویه تراکنش‌های در حال انجام',
    price: stats.value.sum_ongoing_transactions?.value ?? 0,
    currency: 'TOMAN',
  },
  {
    label: 'جمع مقدار درآمد تومانی (ماه جاری)',
    price: stats.value.sum_toman_income?.value ?? 0,
    currency: 'TOMAN',
  },
  {
    label: 'جمع مقدار درآمد ارزی (ماه جاری)',
    price: parseFloat((stats.value.sum_currency_income?.value ?? 0).toFixed(3)),
    currency: 'COUNT',
  },
  {
    label: 'متوسط درصد کارمزد دریافتی به ازای تراکنش (ماه جاری)',
    price: parseFloat((stats.value.average_currency_income?.value ?? 0).toFixed(3)),
    currency: 'COUNT',
  },
  {
    label: 'اعتبار حساب کاربران پی‌جیب',
    price: stats.value.sum_user_credit?.value ?? 0,
    currency: 'TOMAN',
  },
  {
    label: 'تعداد تخفیف‌های استفاده شده (ماه جاری)',
    price: stats.value.total_discounts?.value ?? 0,
    currency: 'COUNT',
    linkLabel: 'مشاهده‌ی تخفیف‌ها',
    linkTo: '/settings/discounts?used=isUsed&page=1',
  },
  {
    label: 'تعداد تخفیف‌های معتبر استفاده‌نشده',
    price: stats.value.total_active_discounts?.value ?? 0,
    currency: 'COUNT',
    linkLabel: 'مشاهده‌ی تخفیف‌ها',
    linkTo: '/settings/discounts?used=notUsed&page=1',
  },
  {
    label: 'جمع مقدار ریالی تخفیف‌های ارائه‌شده (ماه جاری)',
    price: stats.value.sum_transaction_discounts?.value ?? 0,
    currency: 'TOMAN',
  },
])

//  Request and verification statuses (bottom section)
const requestStatuses = computed(() => [
  // {
  //   label: "پرونده‌های در انتظار تایید مدارک قدیمی",
  //   linkUrl: "/users?isOld=true&page=1",
  //   count: stats.value.total_identification_verification_old_pending_users?.value ?? 0,
  // },
  {
    label: 'پرونده‌های در انتظار تایید مدارک',
    linkUrl: '/users?status=IDENTIFICATION_VERIFICATION_PENDING&page=1',
    count: stats.value.total_identification_verification_pending_users?.value ?? 0,
  },
  {
    label: 'درخواست‌ها با پیشنهاد بی‌پاسخ',
    linkUrl: '/requisitions?hasNoOffer=true&page=1',
    count: stats.value.total_requisitions_with_pending_offers?.value ?? 0,
    newTab: true,
  },
  {
    label: 'تراکنش‌های در انتظار تایید طرفین',
    linkUrl: '/transactions?status=INITIAL_OWNER_CONFIRMATION_PENDING&page=1',
    count: stats.value.total_initial_owner_confirmation_pending_transactions?.value ?? 0,
  },
  {
    label: 'تراکنش‌های در انتظار پرداخت خریدار',
    linkUrl: '/transactions?status=BUYER_PAYMENT_PENDING&page=1',
    count: stats.value.total_buyer_payment_pending_transactions?.value ?? 0,
  },
  {
    label: 'تراکنش‌های در انتظار تایید پرداخت خریدار',
    linkUrl: '/transactions?status=BUYER_PAYMENT_VERIFICATION_PENDING&page=1',
    count: stats.value.total_buyer_payment_verification_pending_transactions?.value ?? 0,
  },
  {
    label: 'تراکنش‌های در انتظار پرداخت فروشنده',
    linkUrl: '/transactions?status=SELLER_PAYMENT_PENDING&page=1',
    count: stats.value.total_seller_payment_pending_transactions?.value ?? 0,
  },
  {
    label: 'تراکنش‌های در انتظار تایید پرداخت فروشنده',
    linkUrl: '/transactions?status=SELLER_PAYMENT_VERIFICATION_PENDING&page=1',
    count: stats.value.total_seller_payment_verification_pending_transactions?.value ?? 0,
  },
  {
    label: 'تراکنش‌های در انتظار تسویه پی‌جیب',
    linkUrl: '/transactions?status=SETTLE_WITH_SELLER_PENDING&page=1',
    count: stats.value.total_settle_with_seller_pending?.value ?? 0,
  },
])
</script>

<template>
  <div class="w-full mb-12">
    <!-- 👉 Financial Overview Section -->
    <div class="w-full">
      <h2 class="text-[22px] text-[#1b325a ] mb-7">وضعیت مالی</h2>
      <div class="w-full flex flex-wrap gap-6">
        <StatusCard v-for="(stat, index) in dashboardMetrics" :key="`stat-item-${index}`" :data="stat"
          class="w-full md:w-[calc(19.5%-1rem)]" />
      </div>
    </div>

    <!-- 👉 Requests & Cases Section -->
    <div class="w-full">
      <h2 class="text-[22px] text-[#1b325a ] mb-7 mt-8">وضعیت پرونده‌ها و درخواست‌ها</h2>
      <div class="w-full flex flex-wrap gap-6">
        <StatusSummaryCard v-for="(stat, index) in requestStatuses" :key="`stat-item-${index}`" :item="stat"
          class="w-full lg:w-[calc(24.5%-1rem)]" />
      </div>

    </div>

  </div>
</template>
